import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import { sessionStorage } from './storage'
import type { ApiResult } from '@/api/common'
import router from '../router'
import { LanguageManager } from '@/utils/language'

// const baseURL = import .meta.env.VITE_APP_BASE_API

// 创建 axios 实例
const service: AxiosInstance = axios.create({
    // 开发环境使用相对路径，让 Vite 代理生效
    baseURL: "",  // 若不设置 baseURL，使用相对路径
    timeout: 50000,
    withCredentials: false, // 禁止发送 Cookie
    headers: { 'Content-Type': 'application/json;charset=utf-8' }
})

// 请求拦截器
service.interceptors.request.use(
    config => {
        config.withCredentials = false
        const currentLanguage = LanguageManager.getCurrentLanguage()
        switch (currentLanguage) {
          case 'zh_TW':
            config.headers['Accept-Language'] = 'zh-MO'
            break
          case 'en_US':
            config.headers['Accept-Language'] = 'en-US'
            break
          case 'pt_PT':
            config.headers['Accept-Language'] = 'pt-PT'
            break
        }
        const info = sessionStorage.get('userInfo')
        if (info) { 
            // 添加 token
            const token = sessionStorage.get('token'+info?.id)
            if (token) {
                config.headers['token'] = `${token}`
            }
            if (info?.id) {
                config.headers['userId'] = `${info.id}`
            }
        }
        
        // 添加当前选中的学校 ID
        const currentSchool = sessionStorage.get('currentSchool')
        if (currentSchool?.id) {
            // console.log('currentSchool', currentSchool.id)
            config.headers['schoolId'] = currentSchool.id
        }
        
        // 处理 GET 请求参数编码
        if (config.method?.toLowerCase() === 'get' && config.params) {
            let url = config.url += '?'
            const params = { ...config.params }
            for (const key in params) {
                const value = params[key]
                if (value !== undefined && value !== null) {
                  //   console.log('value', value)
                    url += `${key}=${encodeURIComponent(value)}&`
                }
            }
            url = url.slice(0, -1)
            config.params = {}
            config.url = url
        }
        return config
    },
    error => {
        return Promise.reject(error)
    }
)

// 处理401未授权的统一方法
const handleUnauthorized = () => {
   //  console.log('status', status)

    // 清除所有存储
    sessionStorage.clear()
    localStorage.clear()
    
    // 如果当前不在登录页，则跳转到登录页
    if (router.currentRoute.value.path !== '/login') {
        router.replace({
            path: '/login',
        })
    }
}

// 错误消息映射
const errorMessages: Record<number, string> = {
  400: '请求参数错误',
  403: '没有访问权限',
  404: '请求的资源不存在',
  500: '服务器内部错误',
  502: '网关错误',
  503: '服务不可用',
  504: '网关超时'
}

// 错误处理
const handleError = (error: any) => {
  const message = errorMessages[error.response?.status] || error.message || '请求失败'
  ElMessage.closeAll();
  ElMessage.error(message)
  return Promise.reject(error)
}

// 响应拦截器
service.interceptors.response.use(
    response => {
        const res = response.data as ApiResult
        if (res.error) {
            // 判断业务状态码
            if (res.error !== 10000) {
                ElMessage.closeAll();
                ElMessage.error(res.message || '请求失败')
               //  console.log('res', res.error)
               //  console.log('res', res)
                // 如果是401未授权，则处理登录失效
                if (res.error == 401 || res.error == 502) {
                    handleUnauthorized()
                    return
                }
                
                return Promise.reject(new Error(res.message || '请求失败'))
            }
            
            return res.data
        } else {
            return res
        }
    },
    error => {
        // 处理401等错误
        if (!error.response) {
            ElMessage.closeAll();
            ElMessage.error('網路連線失敗，請檢查網路設定')
            
            // console.log('error', error)
            return Promise.reject(new Error('網路連線失敗'))
        }
      // 处理401等错误
      const status = error.response?.status
      if (status === 401) {
          handleUnauthorized()
          return Promise.reject(error)
      } else if (status === 502) {
          handleUnauthorized()
          return Promise.reject(error)
      }
      
      // 处理其他HTTP错误
      const message = errorMessages[status] || error.message || '请求失败'
      ElMessage.closeAll();
      ElMessage.error(message)
      return Promise.reject(error)
    }
)

// 封装请求方法
export function request<T = any>(config: AxiosRequestConfig) {
    return service(config) as Promise<any>
} 